import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { networkLabel } from "@/lib/networkLabels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, ArrowSquareOut, Storefront, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ReplicateAssignmentsDialog } from "./ReplicateAssignmentsDialog";


/** Stable card order — must match backend `COVERAGE_NETWORKS`. */
const COVERAGE_NETWORK_ORDER = [
  "impact", "partnerize", "skimlinks", "mrge", "cj", "admedia",
  "pubadhub", "pubadhub_kevin", "flexoffers", "shopnomix_kevin", "siteplug", "maxbounty", "cashbackhive", "fatcoupon",
  "shopnomix", "pronomix", "giddyup", "rga_vishy", "rga_ashok",
];

const NETWORK_COLORS = {
  impact: "bg-blue-50 text-blue-700 border-blue-200",
  partnerize: "bg-emerald-50 text-emerald-700 border-emerald-200",
  skimlinks: "bg-orange-50 text-orange-700 border-orange-200",
  mrge: "bg-purple-50 text-purple-700 border-purple-200",
  cj: "bg-amber-50 text-amber-700 border-amber-200",
  admedia: "bg-violet-50 text-violet-700 border-violet-200",
  pubadhub: "bg-rose-50 text-rose-700 border-rose-200",
  pubadhub_kevin: "bg-teal-50 text-teal-700 border-teal-200",
  flexoffers: "bg-indigo-50 text-indigo-700 border-indigo-200",
  shopnomix_kevin: "bg-pink-50 text-pink-700 border-pink-200",
  siteplug: "bg-sky-50 text-sky-700 border-sky-200",
  maxbounty: "bg-cyan-50 text-cyan-700 border-cyan-200",
  cashbackhive: "bg-teal-50 text-teal-700 border-teal-200",
  fatcoupon: "bg-rose-50 text-rose-700 border-rose-200",
  giddyup: "bg-yellow-50 text-yellow-700 border-yellow-200",
  rga: "bg-violet-50 text-violet-700 border-violet-200",
  rga_vishy: "bg-violet-50 text-violet-700 border-violet-200",
  rga_ashok: "bg-pink-50 text-pink-700 border-pink-200",
  shopnomix: "bg-lime-50 text-lime-700 border-lime-200",
  pronomix: "bg-purple-50 text-purple-700 border-purple-200",
};

/**
 * Brand Coverage Panel — per-publisher view of which feed-partner brands
 * have been assigned vs. left untouched. Each network gets a card with
 * a progress bar and a "Manage" button that opens a paged drill-down
 * dialog (assigned / unassigned / all + bulk-assign / bulk-unassign).
 *
 * Drives the operational question "who's missing what?" — Step 2 of the
 * 2026-05-07 Brand Coverage feature.
 */
export function BrandCoveragePanel({ publisherId, publisherEmail, assignedBrands = [], onAssignmentsChanged }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeNetwork, setActiveNetwork] = useState(null);
  const [replicateOpen, setReplicateOpen] = useState(false);
  const [networkFilter, setNetworkFilter] = useState("all");

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/publishers/${publisherId}/brand-coverage`);
      setSummary(data);
    } catch (e) {
      toast.error("Failed to load brand coverage");
    } finally {
      setLoading(false);
    }
  }, [publisherId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const emptyRow = { total: 0, assigned: 0, unassigned: 0 };

  const mergeRow = (net) => {
    const apiRow = summary?.by_network?.[net] || emptyRow;
    const localAssigned = assignedBrands.filter(
      (b) => (b.network || "").toLowerCase() === net
    ).length;
    const assigned = Math.max(apiRow.assigned || 0, localAssigned);
    const total = Math.max(apiRow.total || 0, assigned);
    return {
      assigned,
      total,
      unassigned: Math.max(0, total - assigned),
    };
  };

  const networks = COVERAGE_NETWORK_ORDER.filter(
    (n) => networkFilter === "all" || n === networkFilter
  );
  const totalAssigned = COVERAGE_NETWORK_ORDER.reduce(
    (s, n) => s + mergeRow(n).assigned,
    0
  );

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="brand-coverage-panel">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Storefront size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Brand Coverage by Feed Partner</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={networkFilter} onValueChange={setNetworkFilter}>
            <SelectTrigger
              className="h-7 w-[160px] text-xs bg-white"
              data-testid="coverage-network-filter"
            >
              <SelectValue placeholder="All feed partners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All feed partners</SelectItem>
              {COVERAGE_NETWORK_ORDER.map((n) => (
                <SelectItem key={n} value={n}>
                  {networkLabel(n)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={totalAssigned === 0}
            onClick={() => setReplicateOpen(true)}
            data-testid="coverage-replicate-btn"
            title={totalAssigned === 0 ? "Assign at least one brand first" : "Copy this pub's full setup to other publishers"}
          >
            <Copy size={12} className="mr-1" /> Replicate to other pubs
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={fetchSummary} data-testid="coverage-refresh-btn">
            <ArrowsClockwise size={12} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {loading && <div className="col-span-full text-sm text-slate-400 py-4">Loading…</div>}
        {!loading && networks.length === 0 && (
          <div className="col-span-full text-sm text-slate-400 py-4">No coverage data</div>
        )}
        {!loading && networks.map(net => {
          const row = mergeRow(net);
          const pct = row.total > 0 ? Math.round((row.assigned / row.total) * 100) : 0;
          return (
            <div
              key={net}
              className="border border-slate-200 rounded-md p-3 hover:border-slate-300 transition-colors"
              data-testid={`coverage-card-${net}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${NETWORK_COLORS[net] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                  {networkLabel(net)}
                </span>
                <span className="text-xs font-mono text-slate-500" data-testid={`coverage-pct-${net}`}>{pct}%</span>
              </div>
              <div className="text-2xl font-mono font-semibold text-slate-900">
                {row.assigned}<span className="text-slate-400 text-base"> / {row.total}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {row.unassigned.toLocaleString()} unassigned
              </div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded overflow-hidden">
                <div
                  className="h-full bg-[#002FA7] transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-3 h-7 text-xs"
                disabled={row.total === 0 && row.assigned === 0}
                onClick={() => setActiveNetwork(net)}
                data-testid={`coverage-manage-btn-${net}`}
              >
                Manage <ArrowSquareOut size={12} className="ml-1" />
              </Button>
            </div>
          );
        })}
      </div>

      <BrandCoverageDialog
        publisherId={publisherId}
        network={activeNetwork}
        onClose={() => setActiveNetwork(null)}
        onAssigned={() => {
          fetchSummary();
          if (onAssignmentsChanged) onAssignmentsChanged();
        }}
      />

      <ReplicateAssignmentsDialog
        open={replicateOpen}
        onClose={() => setReplicateOpen(false)}
        sourcePublisher={{ id: publisherId, email: publisherEmail }}
        sourceAssignmentCount={totalAssigned}
        onReplicated={() => {
          fetchSummary();
          if (onAssignmentsChanged) onAssignmentsChanged();
        }}
      />
    </div>
  );
}


/**
 * Brand Coverage dialog — assigned / unassigned / all + bulk assign/unassign.
 * Assigned rows are selectable so admins can drop over-assigned catalogs
 * (e.g. Shopnomix) without leaving this modal.
 */
function BrandCoverageDialog({ publisherId, network, onClose, onAssigned }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [status, setStatus] = useState("unassigned");
  const [q, setQ] = useState("");
  const [popularOnly, setPopularOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const open = !!network;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Reset on network change.
  useEffect(() => {
    if (!network) return;
    setItems([]);
    setSelected(new Set());
    setPage(1);
    setStatus("unassigned");
    setQ("");
    setPopularOnly(false);
  }, [network]);

  const fetchPage = useCallback(async () => {
    if (!network) return;
    setLoading(true);
    try {
      const { data } = await API.get(
        `/admin/publishers/${publisherId}/brand-coverage/${network}`,
        { params: { status, q, page, page_size: pageSize, popular_only: popularOnly } }
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  }, [publisherId, network, status, q, page, pageSize, popularOnly]);

  useEffect(() => { if (open) fetchPage(); }, [fetchPage, open]);

  const toggleRow = (cid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      // Assigned tab → select assigned; Unassigned → available; All → everything on page.
      items.forEach(i => {
        if (status === "assigned" && !i.is_assigned) return;
        if (status === "unassigned" && i.is_assigned) return;
        next.add(i.campaign_id);
      });
      return next;
    });
  };

  const selectAllMatching = async () => {
    if (!network || total === 0) return;
    if (total > 20000) {
      toast.error("Too many brands to select at once (20,000 max). Narrow the search first.");
      return;
    }
    const label = status === "assigned" ? "assigned" : status === "unassigned" ? "unassigned" : "";
    if (!window.confirm(`Select all ${total.toLocaleString()} ${label} brand${total === 1 ? "" : "s"}?`)) {
      return;
    }
    setSelectingAll(true);
    try {
      const { data } = await API.get(
        `/admin/publishers/${publisherId}/brand-coverage/${network}`,
        { params: { status, q, popular_only: popularOnly, ids_only: true } }
      );
      const ids = data.campaign_ids || [];
      setSelected(new Set(ids));
      if (data.truncated) {
        toast.warning(`Selected first ${ids.length.toLocaleString()} of ${total.toLocaleString()} brands`);
      } else {
        toast.success(`Selected ${ids.length.toLocaleString()} brands`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to select all brands");
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => setSelected(new Set());

  const submitAssign = async () => {
    const brandIds = Array.from(selected);
    if (brandIds.length === 0) {
      toast.error("Select at least one brand");
      return;
    }
    setAssigning(true);
    try {
      const { data } = await API.post("/admin/coverage/bulk-assign", {
        publisher_id: publisherId,
        brand_ids: brandIds,
      });
      const parts = [`${data.assigned} assigned`];
      if (data.used_derived) parts.push(`${data.used_derived} matched canonical rate`);
      if (data.used_fallback) parts.push(`${data.used_fallback} first-time brand`);
      if (data.skipped_existing) parts.push(`${data.skipped_existing} already assigned`);
      if (data.skipped_loss) parts.push(`${data.skipped_loss} skipped (loss)`);
      if (data.skipped_unparseable) parts.push(`${data.skipped_unparseable} skipped (no rate on record)`);
      toast.success(parts.join(" · "));
      clearSelection();
      fetchPage();
      onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const submitUnassign = async () => {
    const brandIds = Array.from(selected);
    if (brandIds.length === 0) {
      toast.error("Select at least one assigned brand");
      return;
    }
    if (!window.confirm(`Unassign ${brandIds.length} brand${brandIds.length === 1 ? "" : "s"} from this publisher?`)) {
      return;
    }
    setUnassigning(true);
    try {
      const { data } = await API.post("/admin/coverage/bulk-unassign", {
        publisher_id: publisherId,
        brand_ids: brandIds,
      });
      toast.success(`${data.removed} unassigned` + (data.not_found ? ` · ${data.not_found} not found` : ""));
      clearSelection();
      fetchPage();
      onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk unassign failed");
    } finally {
      setUnassigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="coverage-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            Brand Coverage — {networkLabel(network)}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 py-2 border-b border-slate-100">
          <div className="flex items-center gap-1 bg-slate-50 rounded p-0.5">
            {["assigned", "unassigned", "all"].map(s => (
              <button
                key={s}
                onClick={() => { setStatus(s); setPage(1); clearSelection(); }}
                className={`px-3 py-1 text-xs font-medium rounded ${status === s ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500"}`}
                data-testid={`coverage-status-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <Input
            value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="Search brand name…"
            className="h-8 text-sm w-56"
            data-testid="coverage-search"
          />
          {network === "mrge" && (
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={popularOnly}
                onChange={e => { setPopularOnly(e.target.checked); setPage(1); }}
                data-testid="coverage-popular-toggle"
              />
              Popular only
            </label>
          )}
          <span className="ml-auto text-xs text-slate-500" data-testid="coverage-total-count">
            {total.toLocaleString()} brands
          </span>
        </div>

        <div className="flex-1 overflow-auto border border-slate-100 rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Our %</th>
                <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Pub %</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Source</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">No brands match</td></tr>
              )}
              {!loading && items.map(b => {
                const dr = b.derived_rates;
                const ourPct = dr ? dr.base_commission_pct : (b.base_commission_pct || null);
                const pubPctVal = dr ? dr.custom_commission_pct : null;
                const isPassthrough = dr && dr.payout_mode === "passthrough";
                return (
                <tr key={b.campaign_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(b.campaign_id)}
                      onChange={() => toggleRow(b.campaign_id)}
                      data-testid={`coverage-row-cb-${b.campaign_id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{b.name}</div>
                    {b.advertiser_name && b.advertiser_name !== b.name && (
                      <div className="text-xs text-slate-500">{b.advertiser_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {b.is_assigned ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700">Assigned</span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-600">Available</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">
                    {isPassthrough ? <span className="text-slate-400">passthrough</span> : (ourPct ? `${ourPct}%` : <span className="text-slate-400">—</span>)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">
                    {isPassthrough ? <span className="text-slate-400">—</span> : (pubPctVal != null ? `${pubPctVal}%` : <span className="text-slate-400">—</span>)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {dr ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700" title={`${dr.assignment_count} pub${dr.assignment_count === 1 ? "" : "s"} on this brand`}>
                        {dr.assignment_count} pub{dr.assignment_count === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-600">First-time</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2 text-xs">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading} data-testid="coverage-prev">Prev</Button>
            <span className="text-slate-500">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} data-testid="coverage-next">Next</Button>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={selectAllVisible} disabled={loading || selectingAll} data-testid="coverage-select-page">Select page</Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={selectAllMatching}
              disabled={loading || selectingAll || total === 0}
              data-testid="coverage-select-all"
            >
              {selectingAll ? "Selecting…" : `Select all (${total.toLocaleString()})`}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearSelection} disabled={selectingAll}>Clear ({selected.size})</Button>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <div className="flex items-center gap-3 w-full">
            <span className="text-xs text-slate-500" data-testid="coverage-rate-note">
              <span className="font-medium text-slate-700">{selected.size} selected</span>
              <span className="ml-2 text-slate-400">·</span>
              <span className="ml-2">
                {status === "assigned"
                  ? "Unassign removes these brands from this publisher only."
                  : "Rates auto-applied from each brand's existing assignments — first-time brands flagged."}
              </span>
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" onClick={onClose} className="h-8 text-xs" data-testid="coverage-cancel">Cancel</Button>
              {(status === "assigned" || status === "all") && (
                <Button
                  onClick={submitUnassign}
                  disabled={selected.size === 0 || unassigning || assigning}
                  variant="outline"
                  className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="coverage-bulk-unassign"
                >
                  {unassigning ? "Unassigning…" : `Unassign ${selected.size}`}
                </Button>
              )}
              {(status === "unassigned" || status === "all") && (
                <Button
                  onClick={submitAssign}
                  disabled={selected.size === 0 || assigning || unassigning}
                  className="h-8 text-xs bg-[#002FA7] hover:bg-[#0428A0]"
                  data-testid="coverage-bulk-assign"
                >
                  {assigning ? "Assigning…" : `Assign ${selected.size}`}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
